import { AutoComplete, Input, Tag, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Images from 'src/common/Images'
import { Request2 } from 'src/common/Request2'
import api from 'src/common/api'
import { GLOBAL_CLIENT } from 'src/helpers/GlobalClient'
import { useStore } from 'src/hooks'

interface JobHashtagsProps {
   jobId: number
   // Cot jobs.tag: "a, b"
   tag?: string
   canEdit: boolean
   // Bam vao hashtag -> tim cac job co hashtag nay
   onSearchHashtag?: (name: string) => void
   // Ban nho gon de dat trong o cua bang
   compact?: boolean
}

// Giong normalizeHashtag o server: bo dau # o dau, bo dau phay, gop khoang trang
const normalizeHashtag = (value: string) =>
   String(value || '')
      .replace(/^[\s#]+/, '')
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

const isSameTag = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

/**
 * Danh sach hashtag cua 1 job: bam de tim, nut x de go, "+ Them hashtag" de gan (chua co thi server tu tao).
 * Dung chung cho popup chi tiet va bang quan ly viec. Luu ngay, roi cap nhat ca popup lan danh sach.
 */
const JobHashtags: React.FC<JobHashtagsProps> = observer(({ jobId, tag, canEdit, onSearchHashtag, compact }) => {
   const { t } = useTranslation()
   const JobDetailStore = useStore('JobDetailStore')
   const JobsStore = useStore('JobsStore')
   const tags: string[] = String(tag || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)

   const [inputVisible, setInputVisible] = useState(false)
   const [inputValue, setInputValue] = useState('')
   const [options, setOptions] = useState<{ value: string }[]>([])
   const inputRef = useRef<any>(null)
   const searchTimer = useRef<any>(null)
   // Enter tren goi y dang chon ban ca onSelect lan onPressEnter -> chan goi 2 lan
   const isSaving = useRef(false)

   useEffect(() => {
      if (inputVisible) {
         inputRef.current?.focus()
         fetchSuggestions('')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [inputVisible])

   useEffect(() => () => clearTimeout(searchTimer.current), [])

   // Goi y chi de tien go: loi hay chua co hashtag nao thi de trong, khong bao loi
   const fetchSuggestions = (text: string) => {
      clearTimeout(searchTimer.current)
      searchTimer.current = setTimeout(async () => {
         const data = await Request2.getWithTokenNoMessage(api.hashtag.search, { text: normalizeHashtag(text) })
         const hashtags = Array.isArray(data) ? data : []
         setOptions(
            hashtags
               .filter((hashtag: any) => !tags.some((item) => isSameTag(item, hashtag.name)))
               .map((hashtag: any) => ({ value: hashtag.name }))
         )
      }, 300)
   }

   const closeInput = () => {
      setInputVisible(false)
      setInputValue('')
      setOptions([])
   }

   const save = async (url: string, name: string) => {
      const response = await Request2.postWithToken({ name }, url)
      if (response?.data) {
         JobDetailStore.setTagOfJob(jobId, response.data.tag)
         JobsStore.action_setJobTag(jobId, response.data.tag)
      }
   }

   const handleAdd = async (value: string) => {
      const name = normalizeHashtag(value)
      if (!name || isSaving.current || !jobId) return
      if (tags.some((item) => isSameTag(item, name))) {
         closeInput()
         return
      }
      isSaving.current = true
      try {
         await save(api.job.addHashtag + `/${jobId}`, name)
      } finally {
         isSaving.current = false
         closeInput()
      }
   }

   const handleRemove = async (event: React.MouseEvent, name: string) => {
      event.stopPropagation()
      if (isSaving.current || !jobId) return
      isSaving.current = true
      try {
         await save(api.job.removeHashtag + `/${jobId}/remove`, name)
      } finally {
         isSaving.current = false
      }
   }

   const fontSize = compact ? 12 : 14
   const tagStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      background: '#F1F1F1',
      borderRadius: 4,
      border: 'none',
      paddingTop: compact ? 0 : 1,
      paddingBottom: compact ? 0 : 1,
      marginBottom: compact ? 4 : 8
   }

   return (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
         {tags.map((item) => {
            const isLongTag = item.length > 20
            return (
               <Tooltip title={isLongTag ? `#${item}` : t(GLOBAL_CLIENT.timtheohashtag)} key={item}>
                  <Tag
                     style={{ ...tagStyle, cursor: 'pointer' }}
                     onClick={() => onSearchHashtag && onSearchHashtag(item)}
                  >
                     <span style={{ fontSize, fontWeight: 400, lineHeight: '22px', color: '#1890ff' }}>
                        #{isLongTag ? `${item.slice(0, 20)}...` : item}
                     </span>
                     {canEdit && (
                        <img
                           onClick={(event) => handleRemove(event, item)}
                           style={{ marginLeft: 4, height: compact ? 12 : 16 }}
                           src={Images.ic_close_circle}
                           alt="icon"
                        />
                     )}
                  </Tag>
               </Tooltip>
            )
         })}
         {canEdit && inputVisible && (
            <AutoComplete
               value={inputValue}
               options={options}
               onSearch={fetchSuggestions}
               onChange={(value: string) => setInputValue(value)}
               onSelect={(value: string) => handleAdd(value)}
               style={{ width: compact ? 140 : 180, marginBottom: compact ? 4 : 8 }}
            >
               <Input
                  ref={inputRef}
                  size="small"
                  maxLength={100}
                  prefix="#"
                  onPressEnter={() => handleAdd(inputValue)}
                  onBlur={closeInput}
                  style={{ fontSize: 12 }}
               />
            </AutoComplete>
         )}
         {canEdit && !inputVisible && (
            <Tag
               className="site-tag-plus"
               onClick={() => setInputVisible(true)}
               style={{ ...tagStyle, cursor: 'pointer' }}
            >
               <img style={{ marginRight: 4, height: compact ? 14 : 20 }} src={Images.ic_plus} alt="icon" />
               <span style={{ fontSize, fontWeight: 400, lineHeight: '22px', color: '#5c5c5c' }}>
                  {t(GLOBAL_CLIENT.themhashtag)}
               </span>
            </Tag>
         )}
      </div>
   )
})

export default JobHashtags
